import { normalizeHexInput } from './fanCardColors';

export const DEFAULT_APP_PRIMARY = '#00C8FF';
export const DEFAULT_APP_ACCENT = '#000000';

const APP_VARS = ['--hc-app-primary', '--hc-app-primary-light', '--hc-app-accent'];

export const ON_PRIMARY_WHEN_BLACK = '#FFFFFF';

const isNearBlackHex = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.08;
};

// Same fields as the embed: "in-app color" paints accents, "in-app accent" is
// the text and icon color on top of them. Like the mobile app, a near-black
// accent on a near-black color turns white so the text stays readable.
export const resolveAppColors = (branding) => {
  const primary =
    normalizeHexInput(branding?.inApp || branding?.inAppColor) || DEFAULT_APP_PRIMARY;
  const accent =
    normalizeHexInput(branding?.inAppAccent || branding?.inAppAccentColor) ||
    DEFAULT_APP_ACCENT;
  if (isNearBlackHex(primary) && isNearBlackHex(accent)) {
    return { primary, accent: ON_PRIMARY_WHEN_BLACK };
  }
  return { primary, accent };
};

const mixWithWhite = (hex, amount) => {
  const toHex = (n) => (n < 16 ? '0' : '') + n.toString(16).toUpperCase();
  const channel = (from) => {
    const value = parseInt(hex.slice(from, from + 2), 16);
    return toHex(Math.round(value + (255 - value) * amount));
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
};

// Same rule as the embed: the field is a percent, 100 or empty keeps the hex.
const toCssColor = (hex, opacityPercent) => {
  let opacity = opacityPercent == null || opacityPercent === '' ? 100 : Number(opacityPercent);
  if (!Number.isFinite(opacity)) opacity = 100;
  opacity = Math.min(100, Math.max(0, Math.round(opacity)));
  if (opacity >= 100) return hex;
  if (opacity <= 0) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

export const applyAppColors = (root, branding) => {
  if (!root) return;
  if (!branding) {
    APP_VARS.forEach((name) => root.style.removeProperty(name));
    return;
  }
  const { primary, accent } = resolveAppColors(branding);
  root.style.setProperty('--hc-app-primary', toCssColor(primary, branding.inAppOpacity));
  root.style.setProperty('--hc-app-primary-light', mixWithWhite(primary, 0.34));
  root.style.setProperty('--hc-app-accent', toCssColor(accent, branding.inAppAccentOpacity));
};

const brandingCache = new Map();

export const clearBrandingCache = () => brandingCache.clear();

// Paints the school colors on root, or resets them when there is no school or
// the request fails. Branding is fetched once per school; returns a cancel fn.
export const syncSchoolAppColors = (root, schoolId, fetchBranding) => {
  if (!schoolId) {
    applyAppColors(root, null);
    return () => {};
  }
  let cancelled = false;
  if (!brandingCache.has(schoolId)) {
    brandingCache.set(
      schoolId,
      fetchBranding(schoolId).catch((error) => {
        brandingCache.delete(schoolId);
        throw error;
      })
    );
  }
  brandingCache
    .get(schoolId)
    .then((branding) => {
      if (!cancelled) applyAppColors(root, branding);
    })
    .catch(() => {
      if (!cancelled) applyAppColors(root, null);
    });
  return () => {
    cancelled = true;
  };
};
